import { z } from 'zod';
import { v1PaginationQuerySchema, v1PaginationSchema } from './pagination';
import { csvParam, v1SortOrderSchema } from './query';
import { v1PlatformIdFilterDescription, v1PlatformSchema } from './vocabulary';
/**
 * Public coding-agent plugin inventory contract for `/api/v1` (KIR-4431, slice 1).
 *
 * Field names follow `apps/backend/src/routes/v1/NAMING.md`.
 *
 * **On `platforms`.** Internally this resource calls the coding agent a plugin
 * runs in its `source` (`claude_code`, `gemini_cli`, `codex`, …). Publicly it is
 * `platforms`, matching the MCP-server, extension and alert contracts — one
 * public word for "which IDE or agent", whatever each internal module happens to
 * call it. Note the word `source` means something different again on the
 * extension resource (built-in vs added), which is precisely why neither
 * internal name is passed through.
 */
/** One version of a plugin, with how many developers are on it. */
export const v1PluginVersionSchema = z
    .object({
    version: z
        .string()
        .nullable()
        .describe('The version string, or null when the install reported no version.'),
    userCount: z
        .number()
        .int()
        .describe('How many of your endpoint users are on this version.'),
    firstSeenAt: z
        .string()
        .datetime()
        .describe('When this version was first reported, ISO 8601 UTC.'),
})
    .meta({
    id: 'V1PluginVersion',
    title: 'Plugin version',
    description: 'One reported version of a plugin, and its install count.',
    examples: [
        { version: '1.4.0', userCount: 9, firstSeenAt: '2026-07-19T10:26:31Z' },
    ],
});
/**
 * One coding-agent plugin in the organization's inventory.
 *
 * A row is a *plugin*, aggregated across every developer that reported it.
 *
 * **Identity is a name plus a marketplace**, not a name alone — the same plugin
 * name can exist in two marketplaces and they are different rows with different
 * counts. That is why `id` is an opaque encoding of both rather than the name:
 * a name-only identifier could resolve to the wrong row.
 */
export const v1PluginSchema = z
    .object({
    id: z
        .string()
        .describe('Opaque identifier for this plugin within your organization. Stable, and safe to store and pass back to the by-ID and users endpoints. **Do not parse or construct it** — it encodes more than the name, because a plugin name is not unique across marketplaces, and its format is not part of this contract.'),
    name: z
        .string()
        .describe("The plugin's name, as the coding agent reports it."),
    displayName: z
        .string()
        .nullable()
        .describe('The human-readable name, when one was reported. Null when none was.'),
    marketplace: z
        .string()
        .nullable()
        .describe('The marketplace the plugin came from, when known. Null for a plugin installed outside a marketplace.'),
    versions: z
        .array(v1PluginVersionSchema)
        .describe('Every distinct version seen across your endpoint users, with the number of endpoint users on each.'),
    platforms: z
        .array(v1PlatformSchema)
        .describe('The coding agents this plugin was found in, each with the display name the Kirin app shows. Pass `platforms[].id` to the `platform` filter.'),
    userCount: z
        .number()
        .int()
        .describe('How many of your endpoint users have this plugin, across all versions.'),
    firstSeenAt: z
        .string()
        .datetime()
        .describe('When this plugin was first reported, ISO 8601 UTC.'),
    lastSeenAt: z
        .string()
        .datetime()
        .describe('The most recent report of any install of this plugin, ISO 8601 UTC.'),
})
    .meta({
    id: 'V1Plugin',
    title: 'Plugin',
    description: "One coding-agent plugin in your organization's inventory, aggregated across every endpoint user that reported it.",
    examples: [
        {
            id: 'WyJjb2RlLXJldmlldyIsImFudGhyb3BpY3MvY2xhdWRlLWNvZGUtcGx1Z2lucyJd',
            name: 'code-review',
            displayName: 'Code Review',
            marketplace: 'anthropics/claude-code-plugins',
            versions: [
                {
                    version: '1.4.0',
                    userCount: 9,
                    firstSeenAt: '2026-07-19T10:26:31Z',
                },
            ],
            platforms: [{ id: 'claude', name: 'Claude Code' }],
            userCount: 9,
            firstSeenAt: '2026-07-19T10:26:31Z',
            lastSeenAt: '2026-08-31T14:03:07Z',
        },
    ],
});
/** Sortable fields, named for the public field they sort. */
export const v1PluginSortFieldSchema = z
    .enum([
    'name',
    'marketplace',
    'platform',
    'userCount',
    'firstSeenAt',
    'lastSeenAt',
])
    .describe('Which field to sort by.');
/**
 * The list query.
 *
 * The filters the Inventory → Plugins screen offers, per the ticket: name,
 * source and marketplace — `source` being `platform` here, for the reason given
 * at the top of this file.
 *
 * Deliberately unnamed (no `.meta({ id })`): a named schema is emitted as a
 * `$ref`, and `@fastify/swagger` cannot expand a `$ref` into a parameter list.
 */
export const v1PluginListQuerySchema = v1PaginationQuerySchema.extend({
    name: csvParam('Filter by plugin name.'),
    marketplace: csvParam('Filter by marketplace.'),
    platform: csvParam(`Filter by the coding agent the plugin was found in. ${v1PlatformIdFilterDescription}`),
    sortBy: v1PluginSortFieldSchema.default('name'),
    sortOrder: v1SortOrderSchema.default('asc'),
});
export const v1PluginListResponseSchema = z
    .object({
    data: z.array(v1PluginSchema).describe('This page of plugins.'),
    pagination: v1PaginationSchema,
})
    .meta({
    id: 'V1PluginList',
    title: 'Plugin list',
    description: "A page of the coding-agent plugins installed across your organization's endpoint users.",
    examples: [
        {
            data: [
                {
                    id: 'WyJjb2RlLXJldmlldyIsImFudGhyb3BpY3MvY2xhdWRlLWNvZGUtcGx1Z2lucyJd',
                    name: 'code-review',
                    displayName: 'Code Review',
                    marketplace: 'anthropics/claude-code-plugins',
                    versions: [
                        {
                            version: '1.4.0',
                            userCount: 9,
                            firstSeenAt: '2026-07-19T10:26:31Z',
                        },
                    ],
                    platforms: [{ id: 'claude', name: 'Claude Code' }],
                    userCount: 9,
                    firstSeenAt: '2026-07-19T10:26:31Z',
                    lastSeenAt: '2026-08-31T14:03:07Z',
                },
            ],
            pagination: {
                page: 1,
                pageSize: 25,
                totalItems: 31,
                totalPages: 2,
                hasMore: true,
            },
        },
    ],
});
/**
 * One developer who has a given plugin installed.
 *
 * `id` is the same identifier the inventory-users resource publishes, so a
 * caller can join the two.
 */
export const v1PluginUserSchema = z
    .object({
    id: z
        .string()
        .describe('The endpoint user, as identified in the inventory-users resource. Join on this to read their full inventory record.'),
    name: z.string().nullable().describe('Display name, if known.'),
    email: z.string().nullable().describe('Email address, if known.'),
    version: z
        .string()
        .nullable()
        .describe('The version this endpoint user currently has, or null when the install reported none.'),
    platforms: z
        .array(v1PlatformSchema)
        .describe('The coding agents in which this endpoint user has the plugin installed.'),
    lastSeenAt: z
        .string()
        .datetime()
        .describe("This endpoint user's most recent report, ISO 8601 UTC."),
})
    .meta({
    id: 'V1PluginUser',
    title: 'Plugin user',
    description: 'An endpoint user who has a given plugin installed.',
    examples: [
        {
            id: '8c47a0e5-1b93-4d26-b7f8-5e0a2c96d341',
            name: 'Stella Normal',
            email: 'stella@example.com',
            version: '1.4.0',
            platforms: [{ id: 'claude', name: 'Claude Code' }],
            lastSeenAt: '2026-08-31T14:03:07Z',
        },
    ],
});
/**
 * The users of one plugin. Not paginated, for the same reason as the other
 * inventory users lists.
 */
export const v1PluginUserListResponseSchema = z
    .object({
    data: z
        .array(v1PluginUserSchema)
        .describe('Every endpoint user who has this plugin installed.'),
})
    .meta({
    id: 'V1PluginUserList',
    title: 'Plugin users',
    description: 'Every endpoint user in your organization who has a given plugin installed. Not paginated.',
    examples: [
        {
            data: [
                {
                    id: '8c47a0e5-1b93-4d26-b7f8-5e0a2c96d341',
                    name: 'Stella Normal',
                    email: 'stella@example.com',
                    version: '1.4.0',
                    platforms: [{ id: 'claude', name: 'Claude Code' }],
                    lastSeenAt: '2026-08-31T14:03:07Z',
                },
            ],
        },
    ],
});
//# sourceMappingURL=plugins.js.map